import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { PageHeader } from '../components/PageHeader';
import {
  GROUPS,
  SECTION_IDS,
  SCROLL_OFFSET,
  isSectionId,
  scrollToDocsSection,
  type SectionId,
} from './docs/nav';
import { SectionOverview, SectionGettingStarted } from './docs/sections-intro';
import {
  SectionArchitecture,
  SectionRunLifecycle,
} from './docs/sections-architecture';
import { SectionDataModel } from './docs/sections-datamodel';
import {
  SectionProjects,
  SectionIssues,
  SectionRuns,
  SectionLoops,
  SectionPrototypes,
} from './docs/sections-work';
import {
  SectionAgentRoles,
  SectionLoopGates,
  SectionIteration,
  SectionAutoMerge,
} from './docs/sections-pipeline';
import { SectionActivity, SectionMetrics } from './docs/sections-monitor';
import {
  SectionSettings,
  SectionNotifications,
  SectionSecurity,
} from './docs/sections-config';
import { SectionIntegrationApi } from './docs/sections-integrations';

// ─── Section renderer ────────────────────────────────────────────────────────

function renderSection(id: SectionId) {
  switch (id) {
    case 'overview':
      return <SectionOverview />;
    case 'getting-started':
      return <SectionGettingStarted />;
    case 'architecture':
      return <SectionArchitecture />;
    case 'data-model':
      return <SectionDataModel />;
    case 'run-lifecycle':
      return <SectionRunLifecycle />;
    case 'projects':
      return <SectionProjects />;
    case 'issues':
      return <SectionIssues />;
    case 'runs':
      return <SectionRuns />;
    case 'loops':
      return <SectionLoops />;
    case 'prototypes':
      return <SectionPrototypes />;
    case 'agent-roles':
      return <SectionAgentRoles />;
    case 'loop-gates':
      return <SectionLoopGates />;
    case 'iteration':
      return <SectionIteration />;
    case 'automerge':
      return <SectionAutoMerge />;
    case 'activity':
      return <SectionActivity />;
    case 'metrics':
      return <SectionMetrics />;
    case 'settings':
      return <SectionSettings />;
    case 'notifications':
      return <SectionNotifications />;
    case 'security':
      return <SectionSecurity />;
    case 'integration-api':
      return <SectionIntegrationApi />;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function DocsPage() {
  useDocumentTitle('Docs');
  const location = useLocation();
  const activeId = useScrollSpy(SECTION_IDS, SCROLL_OFFSET);

  // Deep-link on first load: jump to the section named by #hash or the legacy
  // ?section= param. Instant (no smooth) so the page opens already positioned.
  useEffect(() => {
    const hash = decodeURIComponent(location.hash.replace(/^#/, ''));
    const param = new URLSearchParams(location.search).get('section');
    const target = isSectionId(hash) ? hash : isSectionId(param) ? param : null;
    if (target) {
      // Defer a frame so every section has mounted and can be measured.
      requestAnimationFrame(() =>
        document.getElementById(target)?.scrollIntoView({ block: 'start' }),
      );
    }
    // Run once on mount — deep links are an entry concern, not a live one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documentation"
        subtitle="Complete reference for using aiai·loop."
      />

      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        {/* Scroll-spy sidebar */}
        <aside className="w-full shrink-0 sm:sticky sm:top-20 sm:max-h-[calc(100vh-6rem)] sm:w-56 sm:overflow-y-auto">
          <nav aria-label="Documentation sections" className="card p-1">
            {GROUPS.map((group) => (
              <div key={group.label}>
                <div className="px-3 pb-1 pt-3 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
                  {group.label}
                </div>
                {group.sections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    aria-current={activeId === s.id ? 'true' : undefined}
                    onClick={() => scrollToDocsSection(s.id)}
                    className={`w-full rounded px-3 py-2 text-left text-sm transition-colors ${
                      activeId === s.id
                        ? 'bg-surface-2 font-medium text-ink'
                        : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        {/* Long-scroll content — one card per group, sections stacked inside. */}
        <main className="min-w-0 flex-1 space-y-6">
          {GROUPS.map((group) => (
            <div key={group.label} className="card space-y-14 px-6 py-6">
              {group.sections.map((s) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  {renderSection(s.id)}
                </section>
              ))}
            </div>
          ))}
        </main>
      </div>
    </div>
  );
}
